export type InvoiceLineForm = {
  item: string
  /** From lab catalog (read-only in UI) */
  description: string
  /** Clinic-added note */
  notes: string
  price: number
  lineDiscount: number
}

/** Lab-configured sub-item from Settings → Lab items */
export type LabPriceListItem = {
  id: string
  caseType: string
  subtype: string
  label: string
  basePrice: number
}

export function applyPriceListItem(
  line: InvoiceLineForm,
  option: LabPriceListItem
): InvoiceLineForm {
  const description = option.subtype
    ? `${option.caseType} — ${option.subtype}`
    : option.caseType
  return {
    ...line,
    item: option.label,
    description,
    price: option.basePrice,
    lineDiscount: 0,
  }
}

export type LabOrderFormValues = {
  labId: string
  labPartnerId: string
  caseTitle: string
  caseMeta: string
  caseDetail: string
  /** FDI tooth numbers or mode tokens: full | upper | lower */
  toothNos: string[]
  patient: string
  patientMeta: string
  orderDate: string
  dueDate: string
  clinicLocation: string
  receivedBy: string
  receivedType: string
  deliveryAddress: string
  contact: string
  invoiceItems: InvoiceLineForm[]
  generalDiscount: number
  deliveryCharge: number
}

export function emptyInvoiceLine(): InvoiceLineForm {
  return { item: '', description: '', notes: '', price: 0, lineDiscount: 0 }
}

export function todayIsoDate() {
  return new Date().toISOString().slice(0, 10)
}

export function clinicSenderDefaults(clinic: {
  name: string
  phone?: string
  address?: string
}): Pick<
  LabOrderFormValues,
  'receivedBy' | 'receivedType' | 'clinicLocation' | 'deliveryAddress' | 'contact'
> {
  const address = clinic.address?.trim() || ''
  const phone = clinic.phone?.trim() || ''
  return {
    receivedBy: clinic.name.trim() || 'Clinic',
    receivedType: 'Delivery',
    clinicLocation: address,
    deliveryAddress: address,
    contact: phone,
  }
}

export function emptyLabOrderValues(): LabOrderFormValues {
  return {
    labId: '',
    labPartnerId: '',
    caseTitle: '',
    caseMeta: '',
    caseDetail: '',
    toothNos: [],
    patient: '',
    patientMeta: '',
    orderDate: todayIsoDate(),
    dueDate: '',
    clinicLocation: '',
    receivedBy: 'Clinic',
    receivedType: 'Delivery',
    deliveryAddress: '',
    contact: '',
    invoiceItems: [],
    generalDiscount: 0,
    deliveryCharge: 0,
  }
}

export function lineNetPrice(line: InvoiceLineForm) {
  return Math.max(0, (Number(line.price) || 0) - (Number(line.lineDiscount) || 0))
}

export function computeOrderTotals(values: LabOrderFormValues) {
  const invoiceItems = values.invoiceItems.map((row) => {
    const price = Number(row.price) || 0
    const lineDiscount = Number(row.lineDiscount) || 0
    return {
      item: row.item.trim(),
      description: row.description.trim(),
      notes: row.notes.trim(),
      price,
      lineDiscount,
      netPrice: Math.max(0, price - lineDiscount),
    }
  })
  const netTotal = invoiceItems.reduce((sum, row) => sum + row.netPrice, 0)
  const generalDiscount = Number(values.generalDiscount) || 0
  const deliveryCharge = Number(values.deliveryCharge) || 0
  const finalTotal = Math.max(0, netTotal - generalDiscount + deliveryCharge)
  return { invoiceItems, netTotal, generalDiscount, deliveryCharge, finalTotal }
}

export function formatDateForApi(isoDate: string) {
  if (!isoDate?.trim()) return undefined
  const d = new Date(`${isoDate}T12:00:00`)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function buildClinicOrderPayload(
  values: LabOrderFormValues,
  options?: { treatmentId?: string },
) {
  const { invoiceItems, netTotal, generalDiscount, deliveryCharge, finalTotal } =
    computeOrderTotals(values)
  const caseTitle = values.caseTitle.trim()
  return {
    labId: values.labId || undefined,
    labPartnerId: values.labPartnerId,
    case: caseTitle,
    caseTitle,
    caseMeta: values.caseMeta.trim() || undefined,
    caseDetail: values.caseDetail.trim() || caseTitle,
    toothNos: values.toothNos.map((t) => String(t).trim()).filter(Boolean),
    patient: values.patient.trim(),
    patientMeta: values.patientMeta.trim() || undefined,
    orderDate: formatDateForApi(values.orderDate),
    dueDate: formatDateForApi(values.dueDate),
    clinicLocation: values.clinicLocation.trim() || undefined,
    receivedBy: values.receivedBy.trim() || 'Clinic',
    receivedType: values.receivedType.trim() || 'Delivery',
    deliveryAddress: values.deliveryAddress.trim() || undefined,
    contact: values.contact.trim() || undefined,
    addedBy: 'Clinic',
    invoiceItems,
    netTotal,
    generalDiscount,
    deliveryCharge,
    finalTotal,
    orderStatus: 'Pending',
    payment: 'Unpaid',
    treatmentId: options?.treatmentId?.trim() || undefined,
  }
}
